import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithCustomToken,
  connectAuthEmulator,
  setPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
} from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

import { app as mainApp } from '../firebase';
import { encryptToken } from '../helper/helper';

let impersonationApp: any = null;
let impersonationAuth: Auth | null = null;
let impersonationFirestore: any = null;
let persistencePromise: Promise<void> | null = null;

/**
 * Initialize a tab-scoped Firebase app instance for impersonation sessions.
 *
 * Uses browserSessionPersistence so the impersonation auth state lives in
 * sessionStorage (per-tab) and never touches IndexedDB. This guarantees that
 * signing in here cannot clobber the admin's main-app session in any other
 * tab on the same origin.
 */
export const initializeImpersonationAuth = () => {
  if (impersonationApp) {
    return { auth: impersonationAuth, firestore: impersonationFirestore };
  }

  try {
    const existingApps = getApps();
    const existingImpersonationApp = existingApps.find((app) => app.name === 'impersonation');

    if (existingImpersonationApp) {
      impersonationApp = existingImpersonationApp;
    } else {
      impersonationApp = initializeApp(mainApp.options, 'impersonation');
    }

    impersonationAuth = getAuth(impersonationApp);

    // Tab-scoped persistence: survives reloads within the tab, never leaks
    // across tabs. Must be set before signInWithCustomToken to take effect.
    persistencePromise = setPersistence(impersonationAuth, browserSessionPersistence).catch((err) => {
      console.error('Failed to set impersonation auth persistence:', err);
    });

    // Mirror the main app's auth emulator connection so custom tokens issued
    // by the server emulator are accepted on localhost.
    const masterEmu = process.env.REACT_APP_USE_EMULATORS === 'true';
    const emuAuthEnv = process.env.REACT_APP_EMU_AUTH;
    const shouldEmuAuth = emuAuthEnv !== undefined ? emuAuthEnv === 'true' : masterEmu;
    if (shouldEmuAuth) {
      connectAuthEmulator(impersonationAuth, 'http://localhost:9099', { disableWarnings: true });
    }

    impersonationFirestore = getFirestore(impersonationApp);

    return { auth: impersonationAuth, firestore: impersonationFirestore };
  } catch (error) {
    console.error('Error initializing impersonation Firebase app:', error);
    throw error;
  }
};

/**
 * Sign in to the tab-scoped impersonation Firebase Auth with a custom token.
 * Returns the Firebase user and an encrypted ID token suitable for use as a
 * Bearer token in API requests.
 */
export const signInImpersonationUser = async (customToken: string) => {
  const { auth } = initializeImpersonationAuth();

  if (!auth) {
    throw new Error('Impersonation auth not initialized');
  }

  if (persistencePromise) {
    await persistencePromise;
  }

  const userCredential: any = await signInWithCustomToken(auth, customToken);
  const idToken = await userCredential.user.getIdToken();
  const encryptedToken = encryptToken(idToken, process.env.REACT_APP_TOKEN_ENCRYPT_KEY!);

  // Wait for the auth state machine to settle (i.e., persistence has been
  // written to sessionStorage and onAuthStateChanged has emitted the new
  // user). Without this barrier, a fast page reload can race against
  // persistence flush, leaving the next page load with no restored user.
  await new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      unsubscribe();
      resolve();
    }, 3000);
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        clearTimeout(timeout);
        unsubscribe();
        resolve();
      }
    });
  });

  return { user: userCredential.user, encryptedToken };
};

/**
 * Sign out from the impersonation Firebase Auth.
 */
export const signOutImpersonationUser = async () => {
  if (!impersonationAuth) {
    return;
  }

  try {
    await impersonationAuth.signOut();
  } catch (error) {
    console.error('Error signing out impersonation user:', error);
    throw error;
  }
};

/**
 * Get the tab-scoped impersonation auth instance, auto-initializing if a
 * session token is present in sessionStorage.
 */
export const getImpersonationAuth = (): Auth | null => {
  if (!impersonationAuth) {
    const token = sessionStorage.getItem('impersonation.token');
    if (token) {
      initializeImpersonationAuth();
    }
  }
  return impersonationAuth;
};
